import React, {ReactElement, useEffect, useRef, useState} from "react"
import {CheckDeposit} from "../../resources/checkDeposits"
import {Resource} from "../../resources/common"
import {AllowedCheckDepositColumns} from "./CheckDeposit.types"
import {Meta} from "../../resources/common"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {useAccessToken} from "../../services/auth"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import Drawer from "../Drawer/Drawer"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import CheckDepositRow from "./CheckDepositRow"
import {CheckDepositColumns} from "./CheckDeposit.types"
import classNames from "classnames"
import {CheckDepositInfo} from "./CheckDepositInfo"

interface CheckDepositsTableProps {
	checkDeposits: Array<CheckDeposit>
	selectedCheckDepositId: string
	setSelectedCheckDepositId: (id: string) => void
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	include?: Resource[]
	includedColumns: Array<AllowedCheckDepositColumns>
	meta: Meta
	token?: string
	refresh: () => void
	fullHeight?: boolean
	disableDrawer?: boolean
	sortFunction: () => void
	sortBy: string
	stickyAction?: boolean
	enableRowClick?: boolean
	showTabsOnHeader?: boolean
}

export default function CheckDepositsTable({
	checkDeposits,
	selectedCheckDepositId,
	setSelectedCheckDepositId,
	includedColumns,
	hasPrev,
	hasNext,
	prev,
	next,
	hasResults,
	isUsingPaging,
	include,
	meta,
	token,
	refresh,
	fullHeight = false,
	disableDrawer = false,
	stickyAction = true,
	sortFunction,
	sortBy,
	enableRowClick = true,
	showTabsOnHeader,
}: CheckDepositsTableProps): ReactElement {
	const accessToken = token ?? useAccessToken()
	const noContent = checkDeposits.length === 0
	const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedCheckDepositId)
	const ref = useRef(null)
	const navigateWithListPath = useNavigateWithListPath()

	const toggleDrawer = (reopenWithId = false) => {
		setIsDrawerOpen((prevState) => !prevState)
		if (reopenWithId) {
			setTimeout(() => {
				setIsDrawerOpen(true)
			}, 200)
		}
	}
	useEffect(() => {
		if (!selectedCheckDepositId) {
			setIsDrawerOpen(false)
		}
	}, [selectedCheckDepositId])

	return (
		<div ref={ref} className={"check-deposits-table"}>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyAction={stickyAction}
				stickyActionWithTabsOnHeader={fullHeight && showTabsOnHeader}
				noContentText={"No check deposits found"}
				clickable={enableRowClick}
				hoverable={enableRowClick}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === CheckDepositColumns.depositDate) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}

							return (
								<DataTableCell
									className={classNames(column[1] === CheckDepositColumns.actions && "has-text-centered")}
									key={column[0]}
								>
									{column[1]}
								</DataTableCell>
							)
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{checkDeposits.map((cd) => (
						<CheckDepositRow
							refresh={refresh}
							onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
								e.preventDefault()
								e.stopPropagation()
								if (enableRowClick) {
									if (disableDrawer) {
										navigateWithListPath(`/check-deposits/${cd.id}`)
									} else {
										setSelectedCheckDepositId(cd.id)
										toggleDrawer(true)
										return
									}
								}
							}}
							selectedCheckDepositId={selectedCheckDepositId}
							include={include}
							key={cd.id}
							cd={cd}
							includedColumns={includedColumns}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>

			{!disableDrawer ? (
				<Drawer
					open={isDrawerOpen}
					onClose={() => {
						toggleDrawer(false)
						setSelectedCheckDepositId("")
					}}
					direction="right"
					className="check-deposits-side-drawer"
					size={"50%"}
					zIndex={33}
					blockBody={false}
					linkToCopy={`${window.location.origin}/check-deposits/${selectedCheckDepositId}`}
				>
					{isDrawerOpen ? (
						<CheckDepositInfo useDrawer refresh={refresh} id={selectedCheckDepositId} token={accessToken} />
					) : null}
				</Drawer>
			) : null}
		</div>
	)
}
